import { BreakerPanelType } from '@bechtel/sr-types';
import 'ag-grid-community/styles/ag-grid.css';
import 'ag-grid-community/styles/ag-theme-alpine.css';
import { Fragment } from 'react';
import { useLanguage } from '~/components/molecules';
import {
  HorizontalLineStyled,
  MarkerStyled,
  TableBodyStyled,
  TableDataStyled,
  TableHeaderRowStyled,
  TableHeaderStyled,
  TableStyled,
  VerticalLineStyled,
} from './styled';

const getColSpan = (breakerPanelType: BreakerPanelType): number => {
  switch (breakerPanelType) {
    case BreakerPanelType.AC_1P2W:
      return 1;
    case BreakerPanelType.AC_1P3W:
    case BreakerPanelType.DC:
      return 2;
    case BreakerPanelType.AC_3P4W:
    default:
      return 3;
  }
};

interface PanelDiagramProps {
  panelType: BreakerPanelType;
  rowCount: number;
}

export const PanelDiagram: React.FC<PanelDiagramProps> = ({ panelType, rowCount }) => {
  const { getMultiLingualTextByKey } = useLanguage();
  return (
    <TableStyled>
      <TableHeaderRowStyled>
        <TableHeaderStyled colSpan={getColSpan(panelType)} style={{ paddingBottom: 8 }}>
          {getMultiLingualTextByKey(panelType === BreakerPanelType.DC ? 'Pole' : 'Phase')}
        </TableHeaderStyled>
      </TableHeaderRowStyled>

      <TableHeaderRowStyled>
        {panelType === BreakerPanelType.AC_1P2W && <TableHeaderStyled>L</TableHeaderStyled>}
        {panelType === BreakerPanelType.AC_1P3W && (
          <>
            <TableHeaderStyled>L1</TableHeaderStyled>
            <TableHeaderStyled>L2</TableHeaderStyled>
          </>
        )}
        {panelType === BreakerPanelType.AC_3P4W && (
          <>
            <TableHeaderStyled>A</TableHeaderStyled>
            <TableHeaderStyled>B</TableHeaderStyled>
            <TableHeaderStyled>C</TableHeaderStyled>
          </>
        )}
        {panelType === BreakerPanelType.DC && (
          <>
            <TableHeaderStyled>P</TableHeaderStyled>
            <TableHeaderStyled>N</TableHeaderStyled>
          </>
        )}
      </TableHeaderRowStyled>
      <TableBodyStyled rowCount={rowCount} style={{ height: rowCount === 0 ? 150 : 'revert-layer' }}>
        <tr>
          <TableDataStyled colSpan={getColSpan(panelType)} rowSpan={rowCount} disabled={true}>
            {panelType === BreakerPanelType.AC_1P2W && (
              <Fragment>
                {[...Array(rowCount)].map((_, index) => (
                  <Fragment key={index}>
                    <HorizontalLineStyled topOffsetIndex={index} />
                    <MarkerStyled markerIndex={index} leftOffset={23} />
                  </Fragment>
                ))}
                <VerticalLineStyled leftOffset={26} rowCount={rowCount} />
              </Fragment>
            )}

            {panelType === BreakerPanelType.DC && (
              <Fragment>
                {[...Array(rowCount)].map((_, index) => (
                  <Fragment key={index}>
                    <HorizontalLineStyled topOffsetIndex={index} />
                    <MarkerStyled markerIndex={index} leftOffset={12 + (index % 2) * 27} />
                  </Fragment>
                ))}
                <Fragment>
                  <VerticalLineStyled leftOffset={15} rowCount={rowCount} />
                  <VerticalLineStyled leftOffset={42} rowCount={rowCount} />
                </Fragment>
              </Fragment>
            )}

            {panelType === BreakerPanelType.AC_1P3W && (
              <Fragment>
                {[...Array(rowCount)].map((_, index) => (
                  <Fragment key={index}>
                    <HorizontalLineStyled topOffsetIndex={index} />
                    <MarkerStyled markerIndex={index} leftOffset={12 + (index % 2) * 33} />
                  </Fragment>
                ))}
                <Fragment>
                  <VerticalLineStyled leftOffset={15} rowCount={rowCount} />
                  <VerticalLineStyled leftOffset={48} rowCount={rowCount} />
                </Fragment>
              </Fragment>
            )}

            {panelType === BreakerPanelType.AC_3P4W && (
              <Fragment>
                {[...Array(rowCount)].map((_, index) => (
                  <Fragment key={index}>
                    <HorizontalLineStyled topOffsetIndex={index} />
                    <MarkerStyled markerIndex={index} leftOffset={12 + (index % 3) * 27} />
                  </Fragment>
                ))}
                <Fragment>
                  <VerticalLineStyled leftOffset={15} rowCount={rowCount} />
                  <VerticalLineStyled leftOffset={42} rowCount={rowCount} />
                  <VerticalLineStyled leftOffset={69} rowCount={rowCount} />
                </Fragment>
              </Fragment>
            )}
          </TableDataStyled>
        </tr>
      </TableBodyStyled>
    </TableStyled>
  );
};
